import React, { useMemo, useState } from "react";
import useForm from "../../../Hooks/useForm";
import useStore from "../../../useStore";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import { LISTAS, FOTO_TAMANOS } from "../../../Constants/Admin";
import { FormSeccionScheme } from "../../../Models/Schemas/form-seccion";
import { seccionAForm, formASeccion } from "../../../Utilities/Admin/seccionForm";
import { setErrorFormSimpleUtil } from "../../../Utilities/Forms/errors";
import Input from "../../Elements/Input/Input";
import Button from "../../Elements/Button/Button";
import { Select, Switch, IconButton } from "./Campos";

// Grupo plegable del formulario
const Grupo = ({ titulo, icono, abierto, children, resumen }) => (
  <details className="pp-grupo" open={abierto}>
    <summary>
      <i className={`bi ${icono}`} />
      <span>{titulo}</span>
      {resumen && <small>{resumen}</small>}
    </summary>
    <div className="pp-grupo-cuerpo">{children}</div>
  </details>
);

const FormSeccion = ({ seccion, padreIdInicial, cerrar }) => {
  const showToast = useToast();
  const { secciones, guardarSeccion, guardando } = useStore();
  const [intentado, setIntentado] = useState(false);

  const initialValues = useMemo(
    () => (seccion ? seccionAForm(seccion) : { ...seccionAForm(null), padreId: padreIdInicial || "" }),
    [seccion, padreIdInicial]
  );

  const { values, errors, touched, handleChange, handleBlur, setFieldValue, isValid } =
    useForm(initialValues, FormSeccionScheme);

  const esPrincipal = !values.padreId;
  const tieneHijos = seccion && secciones.some((s) => s.padreId === seccion.seccionId);

  const opcionesPadre = [
    { value: "", label: "— Es una sección principal —" },
    ...secciones
      .filter((s) => !s.padreId && s.seccionId !== seccion?.seccionId)
      .map((s) => ({ value: s.seccionId, label: `Dentro de: ${s.nombre}` })),
  ];

  const err = (key) => setErrorFormSimpleUtil(errors, touched, key);
  // Errores de listas (precios, extras, fotos): se muestran al intentar guardar
  const errLista = (lista, i, campo) => (intentado ? errors[lista]?.[i]?.[campo] : undefined);

  // --- Helpers para editar listas ---
  const editarFila = (lista, i, campo, valor) =>
    setFieldValue(lista, values[lista].map((f, idx) => (idx === i ? { ...f, [campo]: valor } : f)));
  const quitarFila = (lista, i) => setFieldValue(lista, values[lista].filter((_, idx) => idx !== i));
  const agregarFila = (lista, fila) => setFieldValue(lista, [...values[lista], fila]);
  const moverFila = (lista, i, dir) => {
    const copia = [...values[lista]];
    const j = i + dir;
    if (j < 0 || j >= copia.length) return;
    [copia[i], copia[j]] = [copia[j], copia[i]];
    setFieldValue(lista, copia);
  };

  const submit = async () => {
    setIntentado(true);
    if (!isValid) {
      showToast("warning", "Revisa los campos marcados en rojo");
      return;
    }
    const r = await guardarSeccion(formASeccion(values), seccion?.seccionId);
    showToast(r.ok ? "success" : "error", r.ok ? (seccion ? "Sección actualizada ✅" : "Sección creada 🪐") : r.message);
    if (r.ok) cerrar();
  };

  return (
    <form
      className="pp-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* GENERAL */}
      <Grupo titulo="General" icono="bi-info-circle" abierto>
        <Input
          label="Nombre"
          value={values.nombre}
          placeholder="Ej. Hamburguesas"
          onChange={handleChange("nombre")}
          onBlur={handleBlur("nombre")}
          error={err("nombre") || (intentado ? errors.nombre : undefined)}
          maxLength={60}
        />
        <Select
          label="Ubicación"
          value={values.padreId}
          onChange={(v) => setFieldValue("padreId", v)}
          opciones={opcionesPadre}
          disabled={tieneHijos}
          ayuda={
            tieneHijos
              ? "Tiene subsecciones, por eso debe seguir siendo principal."
              : "Las principales salen en la barra de navegación. Las subsecciones van dentro de una principal (ej. Alitas dentro de Para Acompañar)."
          }
        />
        {esPrincipal && (
          <div className="pp-form-fila">
            <Input
              label="Texto pequeño arriba del título"
              value={values.eyebrow}
              placeholder="Ej. Comida"
              onChange={handleChange("eyebrow")}
              onBlur={handleBlur("eyebrow")}
              error={err("eyebrow")}
              maxLength={40}
            />
            <Input
              label="Nombre corto en el menú de navegación"
              value={values.navLabel}
              placeholder="Opcional"
              onChange={handleChange("navLabel")}
              onBlur={handleBlur("navLabel")}
              error={err("navLabel")}
              maxLength={30}
            />
          </div>
        )}
        <Switch
          label={values.activa ? "Visible en la página" : "Oculta"}
          checked={values.activa}
          onChange={(v) => setFieldValue("activa", v)}
        />
      </Grupo>

      {/* PRODUCTOS */}
      <Grupo titulo="Cómo se ven los productos" icono="bi-grid-3x2-gap" resumen={LISTAS.find((l) => l.value === values.lista)?.label}>
        <div className="pp-opciones-visuales">
          {LISTAS.map((l) => (
            <button
              key={l.value}
              type="button"
              className={`pp-opcion-visual ${values.lista === l.value ? "activo" : ""}`}
              onClick={() => setFieldValue("lista", l.value)}
            >
              <span className={`pp-mini pp-mini-${l.value}`} aria-hidden="true">
                <i /><i /><i /><i />
              </span>
              {l.label}
            </button>
          ))}
        </div>
        <Input
          label="Título arriba de los productos"
          value={values.itemsTitulo}
          placeholder="Ej. Elige tu sabor"
          onChange={handleChange("itemsTitulo")}
          onBlur={handleBlur("itemsTitulo")}
          error={err("itemsTitulo")}
          maxLength={60}
        />
        <Switch
          label="Mostrar “desde $…” junto al título"
          checked={values.itemsTituloDesde}
          onChange={(v) => setFieldValue("itemsTituloDesde", v)}
        />
      </Grupo>

      {/* PRECIOS */}
      <Grupo
        titulo="Precios de la sección"
        icono="bi-currency-dollar"
        abierto={intentado && Boolean(errors.precios)}
        resumen={values.precios.length ? `${values.precios.length} precio(s)` : "Opcional"}
      >
        <p className="pp-ayuda pp-ayuda-arriba">
          Para cuando el precio no es por producto: tamaños de pizza, la orden de alitas, la malteada… El “Desde $” de la foto toma el menor.
        </p>
        {values.precios.map((p, i) => (
          <div key={i} className="pp-fila-lista">
            <Input label={i === 0 ? "Etiqueta" : undefined} value={p.etiqueta} placeholder="Mediana" onChange={(v) => editarFila("precios", i, "etiqueta", v)} error={errLista("precios", i, "etiqueta")} maxLength={60} />
            <Input label={i === 0 ? "Detalle" : undefined} value={p.detalle} placeholder="8 rebanadas" onChange={(v) => editarFila("precios", i, "detalle", v)} maxLength={60} />
            <Input label={i === 0 ? "Precio" : undefined} value={p.precio} placeholder="135" inputMode="decimal" onChange={(v) => editarFila("precios", i, "precio", v)} error={errLista("precios", i, "precio")} maxLength={8} />
            <div className="pp-fila-acciones">
              <IconButton icon="bi-arrow-up" title="Subir" onClick={() => moverFila("precios", i, -1)} disabled={i === 0} />
              <IconButton icon="bi-trash3" title="Quitar" variante="peligro" onClick={() => quitarFila("precios", i)} />
            </div>
          </div>
        ))}
        <button type="button" className="pp-agregar-fila" onClick={() => agregarFila("precios", { etiqueta: "", detalle: "", precio: "" })}>
          <i className="bi bi-plus-lg" /> Agregar precio
        </button>

        {values.precios.length > 0 && (
          <>
            <Switch
              label="Mostrar tabla de precios (como en las pizzas)"
              checked={values.mostrarTablaPrecios}
              onChange={(v) => setFieldValue("mostrarTablaPrecios", v)}
            />
            {values.mostrarTablaPrecios && (
              <Input label="Título de la tabla" value={values.tituloTabla} placeholder="Precios" onChange={handleChange("tituloTabla")} maxLength={40} />
            )}
          </>
        )}
        <div className="pp-form-fila">
          <Input
            label="Encabezado con precio"
            value={values.encabezado}
            placeholder="Ej. Waffle"
            onChange={handleChange("encabezado")}
            maxLength={60}
          />
          <Input
            label="Texto junto al precio"
            value={values.descripcion}
            placeholder="Ej. Cubierta: lechera, Nutella o miel"
            onChange={handleChange("descripcion")}
            maxLength={200}
          />
        </div>
        <p className="pp-ayuda">Opcional. Se ve como: <strong>Waffle</strong> $75 · Cubierta: lechera…</p>
      </Grupo>

      {/* EXTRAS */}
      <Grupo
        titulo="Extras"
        icono="bi-plus-circle"
        abierto={intentado && Boolean(errors.extrasOpciones)}
        resumen={values.extrasActivo ? `${values.extrasOpciones.length} opción(es)` : "Opcional"}
      >
        <Switch
          label="Mostrar recuadro de extras (ej. orilla de queso Philadelphia)"
          checked={values.extrasActivo}
          onChange={(v) => {
            setFieldValue("extrasActivo", v);
            if (v && !values.extrasOpciones.length) setFieldValue("extrasOpciones", [{ etiqueta: "", precio: "", suma: true }]);
          }}
        />
        {values.extrasActivo && (
          <>
            <div className="pp-form-fila">
              <Input label="Texto" value={values.extrasTitulo} placeholder="Orilla de queso" onChange={handleChange("extrasTitulo")} maxLength={60} />
              <Input label="Texto destacado" value={values.extrasDestacado} placeholder="Philadelphia" onChange={handleChange("extrasDestacado")} maxLength={60} />
            </div>
            {values.extrasOpciones.map((o, i) => (
              <div key={i} className="pp-fila-lista pp-fila-extras">
                <Input label={i === 0 ? "Opción" : undefined} value={o.etiqueta} placeholder="Mediana y Grande" onChange={(v) => editarFila("extrasOpciones", i, "etiqueta", v)} error={errLista("extrasOpciones", i, "etiqueta")} maxLength={60} />
                <Input label={i === 0 ? "Precio" : undefined} value={o.precio} placeholder="40" inputMode="decimal" onChange={(v) => editarFila("extrasOpciones", i, "precio", v)} error={errLista("extrasOpciones", i, "precio")} maxLength={8} />
                <Switch compacto label="Suma (+$)" checked={o.suma} onChange={(v) => editarFila("extrasOpciones", i, "suma", v)} />
                <div className="pp-fila-acciones">
                  <IconButton icon="bi-trash3" title="Quitar" variante="peligro" onClick={() => quitarFila("extrasOpciones", i)} />
                </div>
              </div>
            ))}
            <button type="button" className="pp-agregar-fila" onClick={() => agregarFila("extrasOpciones", { etiqueta: "", precio: "", suma: true })}>
              <i className="bi bi-plus-lg" /> Agregar opción
            </button>
          </>
        )}
      </Grupo>

      {/* FOTOS Y DISEÑO */}
      <Grupo
        titulo="Fotos y diseño"
        icono="bi-image"
        abierto={intentado && Boolean(errors.imagenes)}
        resumen={values.imagenes.length ? `${values.imagenes.length} foto(s)` : "Sin foto"}
      >
        <p className="pp-ayuda pp-ayuda-arriba">
          Pega la URL de la foto (https://…) o una ruta del proyecto (/assets/imgs/…). Con 2 o más fotos se arma un carrusel.
        </p>
        {values.imagenes.map((img, i) => (
          <div key={i} className="pp-fila-lista pp-fila-foto">
            <div className="pp-foto-preview">
              {img.src && /^(https:\/\/|\/)/.test(img.src) ? <img src={img.src} alt="" /> : <i className="bi bi-image" />}
            </div>
            <Input label={i === 0 ? "URL" : undefined} value={img.src} placeholder="https://…" onChange={(v) => editarFila("imagenes", i, "src", v)} error={errLista("imagenes", i, "src")} maxLength={500} />
            <Input label={i === 0 ? "Texto (carrusel)" : undefined} value={img.caption} placeholder="Opcional" onChange={(v) => editarFila("imagenes", i, "caption", v)} maxLength={60} />
            <div className="pp-fila-acciones">
              <IconButton icon="bi-arrow-up" title="Subir" onClick={() => moverFila("imagenes", i, -1)} disabled={i === 0} />
              <IconButton icon="bi-trash3" title="Quitar" variante="peligro" onClick={() => quitarFila("imagenes", i)} />
            </div>
          </div>
        ))}
        <button type="button" className="pp-agregar-fila" onClick={() => agregarFila("imagenes", { src: "", caption: "" })}>
          <i className="bi bi-plus-lg" /> Agregar foto
        </button>

        {values.imagenes.length > 0 && (
          <>
            <div className="pp-form-fila">
              <Select
                label="Foto del lado"
                value={values.fotoLado}
                onChange={(v) => setFieldValue("fotoLado", v)}
                opciones={[
                  { value: "izq", label: "Izquierdo" },
                  { value: "der", label: "Derecho" },
                ]}
              />
              <Select
                label="Ancho de la foto"
                value={values.anchoFoto}
                onChange={(v) => setFieldValue("anchoFoto", v)}
                opciones={[
                  { value: "4", label: "Angosta" },
                  { value: "5", label: "Media" },
                  { value: "6", label: "Mitad" },
                  { value: "7", label: "Ancha" },
                ]}
              />
              <Select label="Alto" value={values.fotoTamano} onChange={(v) => setFieldValue("fotoTamano", v)} opciones={FOTO_TAMANOS} />
            </div>

            {values.imagenes.length === 1 && (
              <>
                <Switch
                  label="Etiqueta de precio sobre la foto"
                  checked={values.overlayActivo}
                  onChange={(v) => setFieldValue("overlayActivo", v)}
                />
                {values.overlayActivo && (
                  <>
                    <div className="pp-form-fila">
                      <Input label="Texto arriba del precio" value={values.overlayLabel} placeholder="Desde" onChange={handleChange("overlayLabel")} maxLength={40} />
                      <Input label="Texto abajo del precio" value={values.overlaySub} placeholder="8 rebanadas" onChange={handleChange("overlaySub")} maxLength={80} />
                    </div>
                    <Switch label="Etiqueta arriba de la foto" checked={values.overlayArriba} onChange={(v) => setFieldValue("overlayArriba", v)} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </Grupo>

      <div className="pp-form-acciones">
        <Button text="Cancelar" color="gray" action={cerrar} padding />
        <Button text={guardando ? "Guardando…" : "Guardar"} color="red" fill action={submit} disabled={guardando} padding />
      </div>
    </form>
  );
};

export default FormSeccion;
